import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import config from '../config';

interface User {
  id: string;
  email: string;
  name: string;
  email_verified?: boolean;
}

interface SubscriptionStatus {
  subscription_status: string;  // trial | active | past_due | cancelled | none
  trial_end_date: string | null;
  subscription_plan: string | null;
  cancel_at_period_end?: boolean;
  scheduled_cancellation_date?: string | null;
  has_payment_method: boolean;
  can_manage_subscription?: boolean;
  trial_expired: boolean;
  certificate_limit?: number | null;
  certificate_limit_kind?: 'trial' | 'month' | null;
  flat_limit?: number | null;
  flat_count?: number | null;
  certificate_count?: number | null;
  certificate_limit_reached?: boolean;
  certificate_limit_resets_on?: string | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  subscription: SubscriptionStatus | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, agreed: boolean) => Promise<{ redirect_to: string }>;
  logout: () => void;
  loading: boolean;
  refreshSubscriptionStatus: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const fetchSubscriptionStatus = useCallback(async (authToken: string) => {
    try {
      const response = await axios.get(`${config.apiUrl}/api/payments/status`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setSubscription(response.data);
    } catch {
      // Non-fatal — subscription status unavailable
    }
  }, []);

  const refreshSubscriptionStatus = useCallback(async () => {
    const savedToken = localStorage.getItem('auth_token');
    if (savedToken) await fetchSubscriptionStatus(savedToken);
  }, [fetchSubscriptionStatus]);

  // Re-reads the signed-in user, e.g. after they confirm their email in another tab
  const refreshUser = useCallback(async () => {
    const savedToken = localStorage.getItem('auth_token');
    if (!savedToken) return;
    try {
      const response = await axios.get(`${config.apiUrl}/api/verify-token`, { headers: { Authorization: `Bearer ${savedToken}` } });
      if (response.data.success) setUser(response.data.user);
    } catch {
      // Keep the current user; this is only a refresh
    }
  }, []);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const savedToken = localStorage.getItem('auth_token');

      if (savedToken) {
        try {
          const response = await axios.get(`${config.apiUrl}/api/verify-token`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });

          if (response.data.success) {
            setToken(savedToken);
            setUser(response.data.user);
            await fetchSubscriptionStatus(savedToken);
          } else {
            localStorage.removeItem('auth_token');
          }
        } catch (error: any) {
          if (error.response && error.response.status === 401) {
            localStorage.removeItem('auth_token');
          } else {
            // Network error — restore from token payload, skip subscription fetch
            console.warn('Token verification network error, keeping session:', error.message);
            setToken(savedToken);
            try {
              const payload = JSON.parse(atob(savedToken.split('.')[1]));
              setUser({ id: payload.user_id || payload.sub, email: payload.email || '', name: payload.name || '' });
            } catch {
              setToken(savedToken);
            }
          }
        }
      }

      setLoading(false);
    };

    checkAuthStatus();
  }, [fetchSubscriptionStatus]);

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post(`${config.apiUrl}/api/login`, { email, password });

      if (response.data.success) {
        const { token, user } = response.data;
        setToken(token);
        setUser(user);
        localStorage.setItem('auth_token', token);
        await fetchSubscriptionStatus(token);
      } else {
        throw new Error(response.data.error || 'Login failed');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message || 'Login failed');
    }
  };

  const register = async (email: string, password: string, name: string, agreed: boolean): Promise<{ redirect_to: string }> => {
    try {
      const response = await axios.post(`${config.apiUrl}/api/register`, { email, password, name, agreed });

      if (response.data.success) {
        const { token, user, redirect_to } = response.data;
        setToken(token);
        setUser(user);
        localStorage.setItem('auth_token', token);
        await fetchSubscriptionStatus(token);
        return { redirect_to: redirect_to || '/payment-setup' };
      } else {
        throw new Error(response.data.error || 'Registration failed');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message || 'Registration failed');
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setSubscription(null);
    localStorage.removeItem('auth_token');
  };

  // apiClient.ts patches window.fetch/axios to dispatch this event the moment ANY request comes
  // back 401 with a token still on file — i.e. the JWT expired mid-session. Previously nothing
  // reacted to that: each page just showed its own generic error with no indication of why.
  useEffect(() => {
    const handleSessionExpired = () => {
      logout();
      navigate('/login', { state: { sessionExpired: true } });
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, [navigate]);

  const value = {
    user,
    token,
    subscription,
    login,
    register,
    logout,
    loading,
    refreshSubscriptionStatus,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
